import { useEffect, useMemo, useState } from 'react';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import { Asterisk, Eye, FolderPlus, ListFilter, Plus, Wand2, X } from 'lucide-react';
import {
  countConditions,
  emptyConditionGroup,
  getRuleBranches,
  type TaxonomyField,
  type TaxonomyRule,
  type TaxonomyRuleActionType,
  type TaxonomyRuleBranch,
  type TaxonomyRuleCondition,
  type TaxonomyRuleConditionGroup,
  type TaxonomyRuleOperator,
} from '@/entities/taxonomy';
import { FormSheet } from '@/shared/ui/FormSheet';
import { MultiSelectDropdown } from '../../../TaxonomyList/components/CreateTaxonomyModal/MultiSelectDropdown';

export interface AddRuleModalProps {
  open: boolean;
  fields: TaxonomyField[];
  /** Existing rule to edit; omit to create a new one. */
  rule?: TaxonomyRule | null;
  onClose: () => void;
  onSave: (rule: TaxonomyRule) => void;
}

// Getters, not values: a resolved label would freeze at import.
const ACTIONS: { value: TaxonomyRuleActionType; label: string; icon: typeof Eye }[] = [
  {
    value: 'limit_options',
    get label() {
      return i18n.t('knowledge:taxonomy.addRuleModal.limitOptionsAction');
    },
    icon: ListFilter,
  },
  {
    value: 'show_field',
    get label() {
      return i18n.t('knowledge:taxonomy.addRuleModal.showField');
    },
    icon: Eye,
  },
  {
    value: 'set_default',
    get label() {
      return i18n.t('knowledge:taxonomy.addRuleModal.setDefault');
    },
    icon: Wand2,
  },
  {
    value: 'require_field',
    get label() {
      return i18n.t('knowledge:taxonomy.addRuleModal.requireField');
    },
    icon: Asterisk,
  },
];

const OPERATORS: { value: TaxonomyRuleOperator; label: string }[] = [
  {
    value: 'is',
    get label() {
      return i18n.t('knowledge:taxonomy.addRuleModal.is');
    },
  },
  {
    value: 'is_not',
    get label() {
      return i18n.t('knowledge:taxonomy.addRuleModal.isNot');
    },
  },
];

const emptyCondition = (): TaxonomyRuleCondition => ({ fieldId: '', operator: 'is', value: '' });

const emptyBranch = (): TaxonomyRuleBranch => ({
  id: crypto.randomUUID(),
  when: { ...emptyConditionGroup(), conditions: [emptyCondition()] },
  action: 'limit_options',
  targetFieldId: '',
  values: [],
  defaultValue: '',
});

/** Value labels a field offers (dropdown values); free-text fields have none. */
/**
 * The `<input type>` a typed default should use, so a date target gets a date
 * picker and a number target a numeric field rather than a bare text box.
 */
function inputTypeFor(field: TaxonomyField | undefined): 'text' | 'number' | 'date' {
  if (field?.type === 'number') return 'number';
  if (field?.type === 'date') return 'date';
  return 'text';
}

function valueOptions(field: TaxonomyField | undefined) {
  return (field?.values ?? [])
    .map((v) => v.value.trim())
    .filter(Boolean)
    .map((v) => ({ label: v, value: v }));
}

/** Every leaf condition (recursively) has a field + value chosen. */
function groupComplete(group: TaxonomyRuleConditionGroup): boolean {
  return group.conditions.every((c) => c.fieldId && c.value) && group.groups.every(groupComplete);
}

/**
 * Recursive editor for a nested AND/OR condition group. Renders the group's
 * combinator toggle (once it holds more than one child), its leaf condition
 * rows, and any nested sub-groups (indented, each recursively editable). The
 * whole subtree is controlled — every mutation produces a new group passed up
 * via `onChange`.
 */
function ConditionGroupEditor({
  group,
  fields,
  fieldOptions,
  onChange,
  onRemove,
  depth,
}: {
  group: TaxonomyRuleConditionGroup;
  fields: TaxonomyField[];
  fieldOptions: { label: string; value: string }[];
  onChange: (next: TaxonomyRuleConditionGroup) => void;
  onRemove?: () => void;
  depth: number;
}) {
  const { t } = useTranslation('knowledge');
  const showCombinator = group.conditions.length + group.groups.length > 1;

  const patchCondition = (i: number, patch: Partial<TaxonomyRuleCondition>) =>
    onChange({
      ...group,
      conditions: group.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    });
  const addCondition = () =>
    onChange({ ...group, conditions: [...group.conditions, emptyCondition()] });
  const removeCondition = (i: number) =>
    onChange({ ...group, conditions: group.conditions.filter((_, idx) => idx !== i) });
  const addGroup = () =>
    onChange({
      ...group,
      groups: [...group.groups, { ...emptyConditionGroup(), conditions: [emptyCondition()] }],
    });
  const patchGroup = (i: number, next: TaxonomyRuleConditionGroup) =>
    onChange({ ...group, groups: group.groups.map((g, idx) => (idx === i ? next : g)) });
  const removeGroup = (i: number) =>
    onChange({ ...group, groups: group.groups.filter((_, idx) => idx !== i) });

  return (
    <div
      className={
        depth > 0
          ? 'flex flex-col gap-2 rounded-lg border border-border-light bg-surface/40 p-2.5'
          : 'flex flex-col gap-2'
      }
    >
      <div className="flex items-center justify-between gap-2">
        {showCombinator ? (
          <ToggleGroup
            aria-label={t('taxonomy.addRuleModal.match')}
            type="single"
            value={group.combinator}
            onValueChange={(v) => v && onChange({ ...group, combinator: v as 'and' | 'or' })}
            variant="outline"
            size="sm"
          >
            {/* eslint-disable-next-line i18next/no-literal-string -- combinator enum */}
            <ToggleGroupItem value="and" className="px-3">
              AND
            </ToggleGroupItem>
            {/* eslint-disable-next-line i18next/no-literal-string -- combinator enum */}
            <ToggleGroupItem value="or" className="px-3">
              OR
            </ToggleGroupItem>
          </ToggleGroup>
        ) : (
          <span className="text-xs text-muted-foreground">{t('taxonomy.addRuleModal.match')}</span>
        )}
        {onRemove && (
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('taxonomy.addRuleModal.removeGroup')}
            className="shrink-0 text-muted-foreground"
            onClick={onRemove}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {group.conditions.map((c, i) => {
        const condField = fields.find((f) => f.id === c.fieldId);
        const opts = valueOptions(condField);
        return (
          <div key={i} className="flex items-center gap-2">
            <Select
              value={c.fieldId}
              onValueChange={(v) => patchCondition(i, { fieldId: v, value: '' })}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder={t('taxonomy.addRuleModal.selectField')} />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={c.operator}
              onValueChange={(v) => patchCondition(i, { operator: v as TaxonomyRuleOperator })}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPERATORS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={c.value}
              onValueChange={(v) => patchCondition(i, { value: v })}
              disabled={!c.fieldId}
            >
              <SelectTrigger className="flex-1">
                <SelectValue
                  placeholder={
                    c.fieldId
                      ? t('taxonomy.addRuleModal.selectValue')
                      : t('taxonomy.addRuleModal.selectFieldFirst')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {opts.length === 0 ? (
                  // eslint-disable-next-line i18next/no-literal-string -- placeholder sentinel
                  <SelectItem value="__none" disabled>
                    {t('taxonomy.addRuleModal.noValuesDefined')}
                  </SelectItem>
                ) : (
                  opts.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="mini"
              aria-label={t('taxonomy.addRuleModal.removeCondition')}
              className="shrink-0 text-muted-foreground"
              disabled={group.conditions.length + group.groups.length <= 1}
              onClick={() => removeCondition(i)}
            >
              <X className="size-4" />
            </Button>
          </div>
        );
      })}

      {group.groups.map((g, i) => (
        <ConditionGroupEditor
          key={i}
          group={g}
          fields={fields}
          fieldOptions={fieldOptions}
          onChange={(next) => patchGroup(i, next)}
          onRemove={() => removeGroup(i)}
          depth={depth + 1}
        />
      ))}

      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="mini"
          className="h-7 w-fit text-xs text-primary"
          onClick={addCondition}
        >
          <Plus className="size-3.5" />
          {t('taxonomy.addRuleModal.addCondition')}
        </Button>
        {depth < 2 && (
          <Button
            variant="ghost"
            size="mini"
            className="h-7 w-fit text-xs text-primary"
            onClick={addGroup}
          >
            <FolderPlus className="size-3.5" />
            {t('taxonomy.addRuleModal.addGroup')}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Create/edit a conditional rule as a DECISION TABLE: one or more branches, each
 * an IF <nested AND/OR condition group> THEN <action> on a target field. A rule
 * with a single branch reads like the original flat rule; extra branches let one
 * rule express e.g. "If Regional require Region AND Country; else if Global set
 * a default" without a rule per permutation.
 */
export function AddRuleModal({ open, fields, rule, onClose, onSave }: AddRuleModalProps) {
  const { t } = useTranslation('knowledge');
  const [branches, setBranches] = useState<TaxonomyRuleBranch[]>([emptyBranch()]);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      // Normalize legacy or multi-branch rules into editable branches.
      setBranches(
        getRuleBranches(rule).map((b) => ({
          ...b,
          values: b.values ?? [],
          defaultValue: b.defaultValue ?? '',
        })),
      );
    } else {
      setBranches([emptyBranch()]);
    }
  }, [open, rule]);

  const fieldOptions = useMemo(
    () => fields.map((f) => ({ label: f.parentId ? `— ${f.name}` : f.name, value: f.id })),
    [fields],
  );

  const patchBranch = (id: string, patch: Partial<TaxonomyRuleBranch>) =>
    setBranches((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const addBranch = () => setBranches((bs) => [...bs, emptyBranch()]);
  const removeBranch = (id: string) =>
    setBranches((bs) => (bs.length > 1 ? bs.filter((b) => b.id !== id) : bs));

  const branchValid = (b: TaxonomyRuleBranch): boolean => {
    const conditionsOk = countConditions(b.when) > 0 && groupComplete(b.when);
    const actionOk =
      b.action === 'limit_options'
        ? (b.values ?? []).length > 0
        : b.action === 'set_default'
          ? !!b.defaultValue
          : true;
    return conditionsOk && !!b.targetFieldId && actionOk;
  };
  const canSave = branches.every(branchValid);

  const handleSave = () => {
    if (!canSave) return;
    // Persist as branches; strip action-config keys that don't apply.
    const cleaned = branches.map((b) => ({
      id: b.id,
      when: b.when,
      action: b.action,
      targetFieldId: b.targetFieldId,
      ...(b.action === 'limit_options' ? { values: b.values ?? [] } : {}),
      ...(b.action === 'set_default' ? { defaultValue: b.defaultValue ?? '' } : {}),
    }));
    onSave({ id: rule?.id ?? crypto.randomUUID(), branches: cleaned });
  };

  return (
    <FormSheet
      className="w-[40rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={rule ? t('taxonomy.addRuleModal.editRule') : t('taxonomy.addRuleModal.addRule')}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {branches.map((branch, bi) => {
          const targetField = fields.find((f) => f.id === branch.targetFieldId);
          const targetValueOptions = valueOptions(targetField);
          return (
            <div
              key={branch.id}
              className="flex flex-col gap-4 rounded-xl border border-border-light bg-card p-4"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {bi === 0
                    ? t('taxonomy.addRuleModal.if')
                    : t('taxonomy.addRuleModal.elseIfBranch', { branch: bi + 1 })}
                </span>
                {branches.length > 1 && (
                  <Button
                    variant="ghost"
                    size="mini"
                    aria-label={t('taxonomy.addRuleModal.removeBranch')}
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => removeBranch(branch.id)}
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>

              {/* IF — nested condition group */}
              <ConditionGroupEditor
                group={branch.when}
                fields={fields}
                fieldOptions={fieldOptions}
                onChange={(when) => patchBranch(branch.id, { when })}
                depth={0}
              />

              {/* THEN — action (dropdown; scales as more actions are added) */}
              <div className="flex flex-col gap-1.5">
                <Label>{t('taxonomy.addRuleModal.then')}</Label>
                <Select
                  value={branch.action}
                  onValueChange={(v) =>
                    patchBranch(branch.id, {
                      action: v as TaxonomyRuleActionType,
                      values: [],
                      defaultValue: '',
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map(({ value, label, icon: Icon }) => (
                      <SelectItem key={value} value={value}>
                        <span className="flex items-center gap-2">
                          <Icon className="size-4" />
                          {label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target field */}
              <div className="flex flex-col gap-1.5">
                <Label>
                  {t('taxonomy.addRuleModal.targetField')}{' '}
                  <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={branch.targetFieldId}
                  onValueChange={(v) =>
                    patchBranch(branch.id, { targetFieldId: v, values: [], defaultValue: '' })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('taxonomy.addRuleModal.selectField')} />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action-specific config */}
              {branch.action === 'limit_options' && (
                <div className="flex flex-col gap-1.5">
                  <Label>
                    {t('taxonomy.addRuleModal.limitOptions')}{' '}
                    <span className="text-destructive">*</span>
                  </Label>
                  {branch.targetFieldId ? (
                    <MultiSelectDropdown
                      value={branch.values ?? []}
                      onChange={(values) => patchBranch(branch.id, { values })}
                      options={targetValueOptions.map((o) => o.value)}
                      placeholder={
                        targetValueOptions.length
                          ? t('taxonomy.addRuleModal.selectValues')
                          : t('taxonomy.addRuleModal.targetFieldHasNoValues')
                      }
                      disabled={targetValueOptions.length === 0}
                    />
                  ) : (
                    <div className="rounded-lg border border-dashed border-border-light px-3 py-2.5 text-sm text-muted-foreground">
                      {t('taxonomy.addRuleModal.selectTargetFieldFirst')}
                    </div>
                  )}
                </div>
              )}

              {branch.action === 'set_default' && (
                <div className="flex flex-col gap-1.5">
                  <Label>
                    {t('taxonomy.addRuleModal.defaultValue')}{' '}
                    <span className="text-destructive">*</span>
                  </Label>
                  {/*
                   * Keyed on whether there is anything to PICK, not on the field's
                   * type.
                   *
                   * A `isConstrained(field)` gate looks more principled and was
                   * tried: it regressed the case it was meant to help. `RulesEditor`
                   * is fed `substituteResolvedValues(fields, resolvedLists)`, so a
                   * bound column whose list has resolved already arrives WITH values
                   * and takes the Select either way — the type test only changed
                   * behaviour when the list had NOT resolved, where it replaced a
                   * usable input with a disabled Select that did not even display
                   * the stored default.
                   *
                   * A target with no vocabulary takes a TYPED default.
                   *
                   * The picker offers every field type, but the control below was
                   * always a `<Select>` fed from the target's values — so choosing
                   * a free-text, number or date column left it reading "Target
                   * field has no values" with nothing to pick, and the rule could
                   * not be completed at all. The engine never cared: it assigns
                   * whatever string it is given. `EditFieldModal` already collects
                   * a field's own default with a plain `Input`, so this is the
                   * same control, not a new idea.
                   */}
                  {branch.targetFieldId && targetValueOptions.length === 0 ? (
                    <Input
                      type={inputTypeFor(targetField)}
                      value={branch.defaultValue ?? ''}
                      onChange={(e) => patchBranch(branch.id, { defaultValue: e.target.value })}
                      placeholder={t('taxonomy.addRuleModal.typeDefaultValue')}
                    />
                  ) : (
                    <Select
                      value={branch.defaultValue ?? ''}
                      onValueChange={(defaultValue) => patchBranch(branch.id, { defaultValue })}
                      disabled={!branch.targetFieldId}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            branch.targetFieldId
                              ? t('taxonomy.addRuleModal.selectValue')
                              : t('taxonomy.addRuleModal.selectTargetFieldFirst')
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {targetValueOptions.length === 0 ? (
                          // Only reachable when a target is chosen and the branch
                          // above did not take the typed input — i.e. never, today.
                          // Kept as a guard rather than deleted.
                          // eslint-disable-next-line i18next/no-literal-string -- placeholder sentinel
                          <SelectItem value="__none" disabled>
                            {t('taxonomy.addRuleModal.targetFieldHasNoValues')}
                          </SelectItem>
                        ) : (
                          targetValueOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {(branch.action === 'show_field' || branch.action === 'require_field') &&
                branch.targetFieldId && (
                  <p className="text-xs text-muted-foreground">
                    {branch.action === 'show_field'
                      ? t('taxonomy.addRuleModal.targetFieldWillOnlyShown')
                      : t('taxonomy.addRuleModal.targetFieldWillRequiredWhen')}
                  </p>
                )}
            </div>
          );
        })}

        <Button variant="outline" size="mini" className="h-8 w-fit text-xs" onClick={addBranch}>
          <Plus className="size-3.5" />
          {t('taxonomy.addRuleModal.addBranch')}
        </Button>
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: rule ? t('taxonomy.addRuleModal.saveRule') : t('taxonomy.addRuleModal.addRule'),
          onClick: handleSave,
          disabled: !canSave,
        }}
        secondary={{ label: t('taxonomy.addRuleModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

AddRuleModal.displayName = 'AddRuleModal';
